import { ErrorCodeEnum, ExpenseKindEnum, type OcrResult, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { type ReadFileAiResponse } from "@finway-group/shared/lib/models/file/file.api"
import { isDeliveryNoteOcrData, isInvoiceOcrData, isPurchaseOrderOcrData } from "@finway-group/shared/lib/utils/ocr.utils"
import * as Sentry from "@sentry/react"
import { type LabeledValue } from "antd/lib/select"
import axios, { isCancel } from "axios"
import HttpStatus from "http-status"
import { ConfirmOCRChangesModal } from "~Components/modals/confirmOCRChanges.modal"
import { i18n } from "~Shared/locales/i18n"
import { CostCenter2Service, CostCenterService, ExpenseService, NotificationService, VendorService } from "~Shared/services"
import { OcrHttpService } from "~Shared/services/ocrHttp.service"
import { type VendorBestMatchRequest } from "~Shared/services/vendor.service"
import { isHospitalityExpense } from "~Shared/utils/expense.utils"
import {
    compareFormOcrData,
    formatOCRObject,
    formatOcrObjectForAutoGeneratedCardRequests,
    mergeExistingAndOcrItems,
    mergeHeaderCostCentersOntoSplitsWhenItemizationDisabled,
    updateOcrItems,
    updatingAttendees,
} from "~Shared/utils/ocr.utils"
import { type BuildOcrUpdateDataParams, type RunOcrAiParams, type RunOcrParams, type SplitDataType, type UpdatedOcrObject } from "~Shared/utils/ocr.utils.types"
import { mergeHeaderCostCentersFromOcrSplits } from "~Shared/utils/ocrHeaderCostCenter.utils"

const errorTitleByCode: { [key: string]: string } = {
    // OCR error
    [ErrorCodeEnum.OCR_NO_DOCUMENT_PROVIDED]: "error:ocr.title",
    [ErrorCodeEnum.OCR_NO_DATA_IN_DOCUMENT]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_INVALID_BASE_64]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_INVALID_URL]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_DOCUMENT_COULD_NOT_BE_LOADED]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_TIMEOUT]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_UNEXPECTED_RESPONSE]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_EMPTY_DOCUMENT]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_INVALID_FILE_ID]: "error:ocr.title",
    [ErrorCodeEnum.OCR_CONTENT_TYPE_UNDETECTABLE]: "error:ocr.title",
    [ErrorCodeEnum.OCR_CONTENT_TYPE_INVALID]: "error:ocr.title",
    [ErrorCodeEnum.OCR_PAGE_RANGE_INVALID]: "error:ocr.title",
    // Error
    [ErrorCodeEnum.OCR_INVALID_DOCUMENT_DATA]: "error:error",
    [ErrorCodeEnum.OCR_INVALID_EINVOICE_TYPE]: "error:error",
    [ErrorCodeEnum.OCR_EINVOICE_DOES_NOT_PASS_VALIDATION]: "error:error",
    [ErrorCodeEnum.OCR_UNIVERSAL_INVOICE_DOES_NOT_PASS_VALIDATION]: "error:error",
    // E-Invoice error
    [ErrorCodeEnum.OCR_ONLY_ONE_DOCUMENT_ALLOWED]: "error:einvoice.title",
    [ErrorCodeEnum.OCR_UNSUPPORTED_EINVOICE_FORMAT]: "error:einvoice.title",
    [ErrorCodeEnum.OCR_INVALID_EINVOICE]: "error:einvoice.title",
    [ErrorCodeEnum.OCR_PREFERRED_PROVIDER_NOT_FOUND]: "error:einvoice.title",
}

export type VendorReadFileAiResponse = ReadFileAiResponse & {
    vendorId?: string
}

function vendorBestMatchRequestFromReadFileAiResponse(ocrData: ReadFileAiResponse["ocrData"]): VendorBestMatchRequest | undefined {
    if (!ocrData) {
        return undefined
    }
    const supplier = isInvoiceOcrData(ocrData) ? ocrData.vendor : ocrData.supplier
    const vendorArgs: VendorBestMatchRequest = { name: supplier?.name }
    switch (true) {
        case isInvoiceOcrData(ocrData):
            vendorArgs.iban = ocrData.vendor?.iban
            break
        case isDeliveryNoteOcrData(ocrData):
            vendorArgs.iban = ocrData.supplier?.vatId
            break
        case isPurchaseOrderOcrData(ocrData):
            vendorArgs.iban = ocrData.supplier?.ibanNumber
            break
        default:
            break
    }
    return vendorArgs
}

export const OcrService = {
    runOcrAi: async (params: RunOcrAiParams): Promise<VendorReadFileAiResponse | undefined> => {
        try {
            params.setIsOcrUploading(true)
            const aiOcrRes = await OcrHttpService.uploadAndRetrieveAi({
                fileId: params.fileId,
                reqData: { documentType: params.documentType },
                abortSignal: params.abortSignal,
            })
            const postOCRResult = await OcrService.postOcrHandler(aiOcrRes)
            return postOCRResult
        } catch (aiOcrError: unknown) {
            if (isCancel(aiOcrError)) {
                return undefined
            }
            NotificationService.showErrorNotificationBasedOnResponseError(aiOcrError, i18n.t("error:ocr.title"))
            return undefined
        } finally {
            params.setIsOcrUploading(false)
        }
    },
    postOcrHandler: async (response: ReadFileAiResponse): Promise<VendorReadFileAiResponse> => {
        const vendorArgs = vendorBestMatchRequestFromReadFileAiResponse(response.ocrData)
        if (!vendorArgs) {
            return { ...response, vendorId: undefined }
        }
        const foundVendor = await OcrService.determineVendor(vendorArgs)
        return { ...response, vendorId: foundVendor?.id }
    },
    determineVendor: async (vendorData: VendorBestMatchRequest) => {
        try {
            const vendor = await VendorService.findBestVendorMatch(vendorData)
            return vendor
        } catch (error) {
            Sentry.captureException(`[OCR Service] error: ${typeof error === "string" ? error : JSON.stringify(error)}`)
            return undefined
        }
    },
    /** @returns `false` if the user cancelled adding the document after being warned about number mismatches, `true` otherwise. */
    runOcr: async ({
        invoiceUrl,
        expense,
        setIsOcrUploading,
        t,
        noDataDifferenceFunction,
        setUpdatedObject,
        showModal,
        language,
        isOcrItemizationEnabled,
        meta,
        isShowingVendorChangeIfNotFound,
        showOcrDifferencesModal = true,
        isOcrRetrigger = false,
        handleInvoice,
    }: RunOcrParams) => {
        try {
            setIsOcrUploading(true)

            const isHospitality = isHospitalityExpense(expense)
            const ocrItemizationEnabled = isOcrItemizationEnabled || isHospitality

            const { ocrData: ocrObject } = await OcrHttpService.uploadAndRetrieveInvoice({
                reqData: { filePath: invoiceUrl, isHospitality, ocrItemizationEnabled, isOcrRetrigger, meta },
                abortSignal: new AbortController().signal,
            })

            const formattedOcrObject = OcrService.formatOcrObjectTaxRate(ocrObject)
            if (!isOcrRetrigger && formattedOcrObject.invoiceNumber) {
                const hasDuplicate = await ExpenseService.checkHasDuplicate(expense, formattedOcrObject.invoiceNumber)
                if (hasDuplicate) {
                    delete formattedOcrObject.invoiceNumber
                }
            }

            const foundVendor = await OcrService.determineVendor({ name: ocrObject.vendor?.name, iban: ocrObject.vendor?.iban })

            try {
                await handleInvoice(ocrObject)
            } catch (err) {
                Sentry.captureException(`[OCR Service (handleInvoice)] Error: ${typeof err === "string" ? err : JSON.stringify(err)}`)
            }

            const formOCRDataDifferences = compareFormOcrData(
                {
                    expense,
                    ocrObject: formattedOcrObject,
                    foundVendor,
                    t,
                    isShowingVendorChangeIfNotFound,
                    resolveCostCenterDisplayName: (id) => CostCenterService.getCostCenterById(id)?.name,
                    resolveCostCenter2DisplayName: (id) => CostCenter2Service.getCostCenter2ById(id)?.name,
                },
                language,
            )

            const shouldNotShowDifferencesModal = !formOCRDataDifferences || !showOcrDifferencesModal

            if (shouldNotShowDifferencesModal) {
                const ocrObjectWithUpdatedSplits = formattedOcrObject.splits
                    ? { ...formattedOcrObject, splits: mergeExistingAndOcrItems(expense.splits, formattedOcrObject.splits, expense.splitType) }
                    : { ...formattedOcrObject }

                return await noDataDifferenceFunction(ocrObjectWithUpdatedSplits)
            }

            showModal(ConfirmOCRChangesModal, true, {
                isShowing: true,
                expense,
                ocrObject: formattedOcrObject,
                formOCRDataDifferences,
                onClose: () => {},
                setUpdatedObject,
            })
            return true
        } catch (err) {
            let title = "error:ocr.title"
            if (axios.isAxiosError(err) && err.response?.status === HttpStatus.BAD_REQUEST) {
                const code = err.response?.data?.errorCode || ""
                if (code && errorTitleByCode[code]) {
                    title = errorTitleByCode[code]
                }
            }

            NotificationService.showErrorNotificationBasedOnResponseError(err, t(title))
            return true
        } finally {
            setIsOcrUploading(false)
        }
    },
    formatOcrObjectTaxRate: (ocrObject: OcrResult) => ({
        ...ocrObject,
        ...(ocrObject.taxRate && { taxRate: ExpenseService.getExpenseTaxRate(ocrObject.taxRate) }),
        ...(ocrObject.splits && {
            splits: ocrObject.splits.map((split) => ({
                ...split,
                ...(split.taxRate && { taxRate: ExpenseService.getExpenseTaxRate(split.taxRate) }),
            })),
        }),
    }),

    buildOcrUpdateData: async ({ ocrObject, existingData, ocrItemizationEnabled, isHospitality, requester }: BuildOcrUpdateDataParams): Promise<UpdatedOcrObject> => {
        const formattedOcrObject = OcrService.formatOcrObjectTaxRate(ocrObject)
        const { vendor } = ocrObject

        let foundVendor
        try {
            foundVendor = await VendorService.findBestVendorMatch({ name: vendor?.name, iban: vendor?.iban })
        } catch (error) {
            Sentry.captureException(`[OCR Service] error: ${typeof error === "string" ? error : JSON.stringify(error)}`)
        }

        const attendees: Array<LabeledValue> | undefined = isHospitality ? updatingAttendees(existingData, requester) : undefined

        const newOcrObject = {
            ...formattedOcrObject,
            ...(isHospitality && attendees && { attendees }),
            ...(foundVendor && { vendor: { ...ocrObject.vendor, _id: foundVendor.id }, vendorType: VendorTypeEnum.VENDOR }),
            // Extract _id for form - formatOcrObject passes full objects for display, form needs IDs
        }

        if (!foundVendor) {
            delete newOcrObject.vendor
        }

        const isSubscription = existingData.kind === ExpenseKindEnum.SUBSCRIPTION
        const isAutoGeneratedCardRequest = existingData.isAutoGenerated && existingData.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && !isSubscription

        const mergeOcrObjectWithSplitUpdate = (base: any, update: SplitDataType) => ({
            ...base,
            ...(update?.splitType !== undefined && { splitType: update.splitType }),
            ...(update?.splits !== undefined && { splits: update.splits }),
        })

        const updatedOcrItems: SplitDataType =
            ocrObject.splits && ocrItemizationEnabled
                ? updateOcrItems({
                      ocrItemizationEnabled,
                      ocrObject: newOcrObject,
                      expense: existingData,
                  })
                : {
                      splits: mergeHeaderCostCentersOntoSplitsWhenItemizationDisabled(existingData.splits, newOcrObject),
                  }

        const ocrObjectWithUpdatedSplits = mergeOcrObjectWithSplitUpdate(newOcrObject, updatedOcrItems)

        if (isAutoGeneratedCardRequest) {
            return mergeHeaderCostCentersFromOcrSplits(formatOcrObjectForAutoGeneratedCardRequests(existingData, ocrObjectWithUpdatedSplits), {
                isHospitality,
                taxRateId: existingData.taxRate?._id,
            })
        }

        return mergeHeaderCostCentersFromOcrSplits(formatOCRObject(ocrObjectWithUpdatedSplits), {
            isHospitality,
            taxRateId: existingData.taxRate?._id,
        })
    },
}
